import axios from 'axios';
import { API_URL } from '../config/constants';
import { getTokenAuth } from '../utils/authUtils';
const roleAdminService = {
    /**
     * Récupérer tous les rôles
     * @returns {Promise<Array>} Liste des rôles
     */
    getAllRoles: async () => {
        const token = getTokenAuth();
        if (!token) return [];

        try {
            const response = await axios.get(`${API_URL}/roles`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data.roles;
        } catch (error) {
            console.error('Error fetching roles:', error);
            return [];
        }
    },

    /**
     * Récupérer un rôle par son ID
     * @param {string} id - ID du rôle
     * @returns {Promise<Object>} Détails du rôle
     */
    getRole: async (id) => {
        const token = getTokenAuth();
        if (!token) return null;

        try {
            const response = await axios.get(`${API_URL}/roles/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data.role;
        } catch (error) {
            console.error(`Error fetching role ${id}:`, error);
            return null;
        }
    },

    // roleAdminService.js

    /**
     * Créer un nouveau rôle
     * @param {Object} roleData - Données du rôle
     * @returns {Promise<Object>} Rôle créé
     */
    createRole: async (roleData) => {
        const token = getTokenAuth();
        if (!token) throw new Error('No authentication token found');

        try {
            const payload = {
                name: roleData.name,
                description: roleData.description,
                isSystem: roleData.isSystem || false,
                isActive: roleData.isActive !== undefined ? roleData.isActive : true,
                displayOrder: roleData.displayOrder || 100,
                icon: roleData.icon || 'Star',
                color: roleData.color || '#607D8B',
                permissions: {
                    attach: roleData.permissions?.attach || [],
                    create: roleData.permissions?.create || []
                }
            };

            const response = await axios.post(`${API_URL}/roles`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data.role;
        } catch (error) {
            console.error('Error creating role:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Mettre à jour un rôle
     * @param {string} id - ID du rôle
     * @param {Object} roleData - Données du rôle
     * @returns {Promise<Object>} Rôle mis à jour
     */
    updateRole: async (id, roleData) => {
        const token = getTokenAuth();
        if (!token) throw new Error('No authentication token found');

        try {
            const payload = {
                name: roleData.name,
                description: roleData.description,
                isActive: roleData.isActive,
                displayOrder: roleData.displayOrder,
                icon: roleData.icon,
                color: roleData.color,
                permissions: {
                    attach: roleData.permissions?.attach || [],
                    create: roleData.permissions?.create || []
                }
            };

            const response = await axios.patch(`${API_URL}/roles/${id}`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data.role;
        } catch (error) {
            console.error(`Error updating role ${id}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Supprimer un rôle
     * @param {string} id - ID du rôle
     * @returns {Promise<Object>} Message de confirmation
     */
    deleteRole: async (id) => {
        const token = getTokenAuth();
        if (!token) throw new Error('No authentication token found');

        try {
            const response = await axios.delete(`${API_URL}/roles/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error(`Error deleting role ${id}:`, error);
            throw error;
        }
    },

    /**
     * Attribuer une permission à un rôle
     * @param {string} roleId - ID du rôle
     * @param {string} permissionId - ID de la permission
     * @returns {Promise<Object>} Message de confirmation
     */
    assignPermissionToRole: async (roleId, permissionId) => {
        const token = getTokenAuth();
        if (!token) throw new Error('No authentication token found');

        try {
            const response = await axios.post(`${API_URL}/roles/${roleId}/permissions`, { permissionId }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error(`Error assigning permission to role ${roleId}:`, error);
            throw error;
        }
    },

    /**
     * Révoquer une permission d'un rôle
     * @param {string} roleId - ID du rôle
     * @param {string} permissionId - ID de la permission
     * @returns {Promise<Object>} Message de confirmation
     */
    revokePermissionFromRole: async (roleId, permissionId) => {
        const token = getTokenAuth();
        if (!token) throw new Error('No authentication token found');

        try {
            const response = await axios.delete(`${API_URL}/roles/${roleId}/permissions/${permissionId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error(`Error revoking permission from role ${roleId}:`, error);
            throw error;
        }
    },

    /**
     * Attribuer un rôle à un utilisateur
     * @param {string} userId - ID de l'utilisateur
     * @param {string} roleId - ID du rôle
     * @returns {Promise<Object>} Message de confirmation
     */
    assignRoleToUser: async (userId, roleId) => {
        const token = getTokenAuth();
        if (!token) throw new Error('No authentication token found');

        try {
            const response = await axios.post(`${API_URL}/roles/assign`, { userId, roleId }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error(`Error assigning role to user:`, error);
            throw error;
        }
    },

    /**
     * Révoquer un rôle d'un utilisateur
     * @param {string} userId - ID de l'utilisateur
     * @param {string} roleId - ID du rôle
     * @returns {Promise<Object>} Message de confirmation
     */
    revokeRoleFromUser: async (userId, roleId) => {
        const token = getTokenAuth();
        if (!token) throw new Error('No authentication token found');

        try {
            const response = await axios.delete(`${API_URL}/roles/revoke/${userId}/${roleId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error(`Error revoking role from user:`, error);
            throw error;
        }
    },

    /**
     * Récupérer les utilisateurs ayant un rôle spécifique
     * @param {string} roleId - ID du rôle
     * @returns {Promise<Array>} Liste des utilisateurs
     */
    getRoleUsers: async (roleId) => {
        const token = getTokenAuth();
        if (!token) return [];

        try {
            const response = await axios.get(`${API_URL}/roles/${roleId}/users`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data.users;
        } catch (error) {
            console.error(`Error fetching users for role ${roleId}:`, error);
            return [];
        }
    }
};

export default roleAdminService;